import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Heading } from './common/Catalyst/heading';
import { useLocation, useNavigate } from 'react-router-dom';
import { WorkOrderReturn, WorkOrderUpdate } from './clients/rust-api-client';
import { homeService, workOrderService } from './clients/ApiClients';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { FilterBar } from './violation/linear/FilterBar';
import { GroupedList } from './violation/linear/GroupedList';
import { saveListContext } from './violation/linear/listContext';
import { useRowUpdate } from './violation/linear/useRowUpdate';
import { updateWorkOrder } from './sync/writes';
import { useListSelection } from './violation/linear/useListSelection';
import { SelectionBar } from './violation/linear/RowCheckbox';
import { canCreateWorkOrder, canEditWorkOrder } from './constants/UserTypes';
import { useCommandPalette } from './shell/CommandPaletteProvider';
import { CreateButton } from './shell/CreateButton';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { useHoaDirectory } from './hoa/useHoaDirectory';
import { SeverityGlyph } from './violation/linear/StatusIcon';
import { WorkOrderRow } from './workorder/linear/WorkOrderRow';
import { WorkOrderStatusIcon } from './workorder/linear/WorkOrderStatusIcon';
import {
  EMPTY_FILTERS,
  PRIORITIES,
  STATUSES,
  WORK_ORDER_TYPES,
  WorkOrderFilters,
  WorkOrderStatus,
  applyFilters,
  groupByStatus,
  normalizeStatus,
  priorityAsSeverity,
  priorityRank,
  statusLabel,
  titleCase,
} from './workorder/linear/workOrderMeta';
import { useSyncedHomes, useSyncedWorkOrders } from './sync/hooks';
import { apiErrorMessage } from './utils/apiErrors';

interface WorkOrderListProps {
  hoaId?: string | null;
  homeId?: string | null;
  /** A member's work orders: those at any of their homes. */
  memberId?: string | null;
  title?: string | null;
  /** Fill the parent's height and scroll inside (the work orders page). */
  fill?: boolean;
  /**
   * With `fill`: no frame of its own, for a page that runs the list to the
   * panel's edges (SidebarLayout `flush`, the work orders page's list view).
   */
  flush?: boolean;
  /** The list's own title row; off where the page already has a heading. */
  showTitle?: boolean;
  /** Rendered at the far right of the filter bar — the view switch. */
  toolbar?: React.ReactNode;
}

const typeOptions: string[] = [...WORK_ORDER_TYPES];

const WorkOrderList: React.FC<WorkOrderListProps> = ({
  hoaId,
  homeId,
  memberId,
  title,
  fill = false,
  flush = false,
  showTitle = true,
  toolbar,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fetchedWorkOrders, setWorkOrders] = useState<WorkOrderReturn[]>([]);
  // The sync engine's local copy when it has one (#561); fetched otherwise.
  // There is no work-orders-by-member endpoint, so a member's are those at
  // their homes — association-wide ones are nobody's in particular.
  const syncedScoped = useSyncedWorkOrders({ hoaId, homeId });
  const syncedMemberHomes = useSyncedHomes({ memberId });
  const syncedWorkOrders = useMemo(() => {
    if (!memberId) return syncedScoped;
    if (!syncedScoped || !syncedMemberHomes) return null;
    const theirs = new Set(syncedMemberHomes.map((h) => h.id));
    return syncedScoped.filter((w) => !!w.home_id && theirs.has(w.home_id));
  }, [memberId, syncedScoped, syncedMemberHomes]);
  const workOrders = syncedWorkOrders ?? fetchedWorkOrders;
  const haveSynced = syncedWorkOrders !== null;
  const [loading, setLoading] = useState(true);
  const [fetchErrorTitle, setFetchErrorTitle] = useState<string>('');
  const [fetchError, setFetchError] = useState<string>('');
  const [showFetchError, setShowFetchError] = useState(false);
  const [filters, setFilters] = useState<WorkOrderFilters>(EMPTY_FILTERS);
  const [reloadKey, setReloadKey] = useState(0);

  // Status and priority change from the row itself (#603).
  const rowUpdate = useRowUpdate<WorkOrderReturn, WorkOrderUpdate>(
    useCallback(
      (workOrderId, workOrderUpdate) => updateWorkOrder(workOrderId, workOrderUpdate),
      []
    ),
    useCallback(
      (updated) =>
        setWorkOrders((prev) => prev.map((w) => (w.id === updated.id ? updated : w))),
      []
    )
  );
  const canEdit = canEditWorkOrder(getUserTypeFromToken());
  const canCreate = canCreateWorkOrder(getUserTypeFromToken());
  const { openNewWorkOrder } = useCommandPalette();

  // Create from the list (#632), whatever its scope: an HOA's or a home's
  // list fixes those chips, a member's offers only that member's homes,
  // the all-work-orders list leaves them to pick.
  const handleCreate = () =>
    openNewWorkOrder({
      ...(hoaId || homeId || memberId
        ? { scope: { hoaId, homeId, memberId }, lockScope: true }
        : {}),
      onCreated: (created) =>
        // One made for "No home" is not the member's, so their list asks
        // again rather than assume.
        memberId
          ? setReloadKey((k) => k + 1)
          : setWorkOrders((prev) => [created, ...prev]),
    });

  useEffect(() => {
    const fetchWorkOrders = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        if (memberId) {
          const homes = (await homeService.getHomesByMember({ memberId })).data;
          const lists = await Promise.all(
            homes.map((h) => workOrderService.getWorkOrdersByHome({ homeId: h.id }))
          );
          setWorkOrders(lists.flatMap((r) => r.data.work_orders));
          return;
        }

        let response;
        if (homeId) {
          response = await workOrderService.getWorkOrdersByHome({ homeId });
        } else if (hoaId) {
          response = await workOrderService.getWorkOrdersByHoa({ hoaId });
        } else {
          response = await workOrderService.getAllWorkOrdersForUser();
        }

        const allWorkOrders = response.data.work_orders;
        setWorkOrders(allWorkOrders);
      } catch (err) {
        console.error('Error fetching work orders:', err);

        const errorMessage = apiErrorMessage(err, "Couldn't load work orders.");

        setFetchErrorTitle("Couldn't load work orders");
        setFetchError(errorMessage);
        setShowFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkOrders();
  }, [hoaId, homeId, memberId, haveSynced, reloadKey]);

  // Filter by HOA (#618) — only where the list spans HOAs. Work orders
  // carry hoa_id, so the directory is only needed for names.
  const spansHoas = !hoaId && !homeId;
  const directory = useHoaDirectory({ enabled: spansHoas, withHomes: false });
  const hoaOptions = useMemo(
    () =>
      Array.from(new Set(workOrders.map((w) => w.hoa_id)))
        .map((id) => ({ value: id, label: directory.hoaName(id) ?? id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [workOrders, directory]
  );

  const visibleWorkOrders = useMemo(
    () => applyFilters(workOrders, filters),
    [workOrders, filters]
  );
  const groups = useMemo(
    () => groupByStatus(visibleWorkOrders),
    [visibleWorkOrders]
  );

  // Checked rows: a change to one of them is made to all of them.
  const selection = useListSelection(visibleWorkOrders);
  const handleRowUpdate = (w: WorkOrderReturn, patch: WorkOrderUpdate, field: string) => {
    const value = patch[field as keyof WorkOrderUpdate];
    // As the row shows them, so a row already at the value is skipped.
    const current = (row: WorkOrderReturn, f: string) =>
      f === 'priority'
        ? (PRIORITIES[priorityRank(row.priority)] ?? titleCase(row.priority))
        : normalizeStatus(row.status);
    rowUpdate.update(selection.targets(w, field, String(value), current), patch, field);
  };


  const handleRowClick = (workOrder: WorkOrderReturn) => {
    // Remember the list as it is shown — filtered and grouped — so the
    // detail page can step through it with ↑/↓ in the same order.
    saveListContext(
      {
        ids: groups.flatMap((g) => g.workOrders.map((w) => w.id)),
        from: location.pathname,
      },
      'workOrder'
    );
    navigate(`/work-orders/${workOrder.id}`);
  };

  // With a synced copy there is nothing to wait for: render it on the
  // first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col' : undefined}>
      {showTitle && (
        <div className="mb-3">
          <Heading level={4}>
            {title || (homeId ? 'Home Work Orders' : 'Work Orders')}
          </Heading>
        </div>
      )}
      {/* Flush against the panel's edges, alerts need their own inset. */}
      <div className={flush ? 'px-3 pt-3 empty:hidden' : undefined}>
        <ShowAlert
          alertType="error"
          alertTitle={fetchErrorTitle}
          alertMessage={fetchError}
          isVisible={showFetchError}
          onClose={() => setShowFetchError(false)}
          timeout={5000}
          isInline={true}
        />
        <ShowAlert
          alertType="error"
          alertTitle="Change not saved"
          alertMessage={rowUpdate.error ?? ''}
          isVisible={rowUpdate.error !== null}
          onClose={rowUpdate.clearError}
          timeout={8000}
          isInline={true}
        />
      </div>
      <div
        className={
          flush
            ? 'flex min-h-0 flex-1 flex-col'
            : fill
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-line bg-raised'
              : 'overflow-hidden rounded-lg border border-line bg-raised'
        }
      >
        <FilterBar
          fields={[
            {
              field: 'Status',
              options: STATUSES.map((st) => ({
                value: st.value,
                label: st.label,
                icon: <WorkOrderStatusIcon status={st.value} />,
              })),
              values: filters.statuses,
              onChange: (v) =>
                setFilters((f) => ({ ...f, statuses: v as WorkOrderStatus[] })),
              label: statusLabel,
            },
            {
              field: 'Priority',
              options: PRIORITIES.map((p) => ({
                value: p,
                label: p,
                icon: <SeverityGlyph severity={priorityAsSeverity(p)} />,
              })),
              values: filters.priorities,
              onChange: (v) => setFilters((f) => ({ ...f, priorities: v })),
            },
            {
              field: 'Type',
              options: typeOptions.map((t) => ({ value: t, label: titleCase(t) })),
              values: filters.types,
              onChange: (v) => setFilters((f) => ({ ...f, types: v })),
            },
            ...(spansHoas
              ? [
                  {
                    field: 'HOA',
                    options: hoaOptions,
                    values: filters.hoas,
                    onChange: (v: string[]) => setFilters((f) => ({ ...f, hoas: v })),
                  },
                ]
              : []),
          ]}
          search={filters.search}
          onSearch={(search) => setFilters((f) => ({ ...f, search }))}
          searchLabel="Search work orders"
          onClearAll={() =>
            setFilters((f) => ({ ...f, statuses: [], priorities: [], types: [], hoas: [] }))
          }
          shown={visibleWorkOrders.length}
          total={workOrders.length}
          trailing={
            <>
              {canCreate && <CreateButton label="New work order" onClick={handleCreate} />}
              {toolbar}
            </>
          }
        />
        {canEdit && (
          <SelectionBar count={selection.selected.size} onClear={selection.clear} />
        )}
        <div className={fill ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          <GroupedList
            groups={groups.map((g) => ({
              key: g.status,
              label: g.label,
              icon: <WorkOrderStatusIcon status={g.status} />,
              items: g.workOrders,
            }))}
            renderRow={(w) => (
              <WorkOrderRow
                key={w.id}
                workOrder={w}
                onOpen={handleRowClick}
                editing={{
                  canEdit,
                  saving: rowUpdate.saving,
                  onUpdate: handleRowUpdate,
                  selection,
                }}
              />
            )}
            idPrefix="work-order-group"
            emptyMessage={
              workOrders.length === 0
                ? 'No work orders yet.'
                : 'No work orders match these filters.'
            }
            selection={canEdit ? { ...selection, getKey: (w) => w.id } : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkOrderList;
